import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import {
  Plus, Loader2, AlertTriangle, CheckCircle2,
  Pencil, Trash2, Upload,
} from 'lucide-react'
import { cn } from '@/lib/utils.ts'
import { Button, Input, EmptyState, LoadingState, Modal, ErrorState } from '@/components/ui'

// ─── Types ────────────────────────────────────────────────────────────────────

interface ArticleUnitTenantLink {
  id: string
  unitId: string
  tenantId: string
  sapAbsEntry: number | null
  syncStatus: 'ok' | 'missing' | 'name_mismatch' | 'error'
  lastSyncAt: string | null
}

interface ArticleUnit {
  id: string
  code: string
  name: string
  isActive: boolean
  sortOrder: number
  syncTenantIds: string[] | null
  tenantLinks: ArticleUnitTenantLink[]
  createdAt: string
  updatedAt: string
}

interface SapTenant {
  id: string
  code: string
  name: string
}

// ─── Status Cell ─────────────────────────────────────────────────────────────

function StatusCell({
  unit,
  tenant,
  onPush,
  pushing,
}: {
  unit: ArticleUnit
  tenant: SapTenant
  onPush: () => void
  pushing: boolean
}) {
  const link = unit.tenantLinks.find((l) => l.tenantId === tenant.id)
  const status = link?.syncStatus

  if (!status || status === 'missing') {
    return (
      <button
        onClick={onPush}
        disabled={pushing}
        className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-500 hover:bg-brand-100 hover:text-brand-700 transition-colors disabled:opacity-50"
        title="In SAP anlegen"
      >
        {pushing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
        Pushen
      </button>
    )
  }
  if (status === 'ok') {
    return (
      <span className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
        <CheckCircle2 className="w-3 h-3" />
        Synced
      </span>
    )
  }
  if (status === 'name_mismatch') {
    return (
      <button
        onClick={onPush}
        disabled={pushing}
        className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-700 hover:bg-yellow-200 transition-colors disabled:opacity-50"
        title="Name in SAP korrigieren"
      >
        {pushing ? <Loader2 className="w-3 h-3 animate-spin" /> : <AlertTriangle className="w-3 h-3" />}
        Korrigieren
      </button>
    )
  }
  return (
    <button
      onClick={onPush}
      disabled={pushing}
      className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-colors disabled:opacity-50"
      title="Erneut versuchen"
    >
      {pushing ? <Loader2 className="w-3 h-3 animate-spin" /> : <AlertTriangle className="w-3 h-3" />}
      Fehler
    </button>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function UnitsSection() {
  const queryClient = useQueryClient()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingUnit, setEditingUnit] = useState<ArticleUnit | null>(null)
  const [pushingCell, setPushingCell] = useState<string | null>(null) // `${unitId}:${tenantId}`
  const [pushingCol, setPushingCol] = useState<string | null>(null) // tenantId

  const { data: units = [], isLoading, error } = useQuery<ArticleUnit[]>({
    queryKey: ['article-units'],
    queryFn: () => apiClient.get('/articles/units').then((r) => r.data),
    retry: false,
  })

  const { data: sapTenants = [] } = useQuery<SapTenant[]>({
    queryKey: ['article-groups-sap-tenants'],
    queryFn: () => apiClient.get('/articles/groups/sap-tenants').then((r) => r.data),
    retry: false,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/articles/units/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['article-units'] }),
  })

  const pushCell = async (unit: ArticleUnit, tenant: SapTenant) => {
    const key = `${unit.id}:${tenant.id}`
    setPushingCell(key)
    try {
      await apiClient.post('/articles/units/sync-to-sap', {
        tenantId: tenant.id,
        tenantCode: tenant.code,
        unitIds: [unit.id],
      })
      queryClient.invalidateQueries({ queryKey: ['article-units'] })
    } catch (err) {
      alert(`Fehler: ${(err as Error).message}`)
    } finally {
      setPushingCell(null)
    }
  }

  const pushColumn = async (tenant: SapTenant) => {
    setPushingCol(tenant.id)
    try {
      await apiClient.post('/articles/units/sync-to-sap', {
        tenantId: tenant.id,
        tenantCode: tenant.code,
      })
      queryClient.invalidateQueries({ queryKey: ['article-units'] })
    } catch (err) {
      alert(`Fehler: ${(err as Error).message}`)
    } finally {
      setPushingCol(null)
    }
  }

  if (isLoading) return <LoadingState label="Lade Maßeinheiten…" />
  if (error) return (
    <ErrorState
      title="Fehler beim Laden der Maßeinheiten"
      description={(error as Error).message}
      details={(error as Error).stack}
      onRetry={() => queryClient.invalidateQueries({ queryKey: ['article-units'] })}
    />
  )

  const hasStkProblem = units.length > 0 && !units.some((u) => u.code === 'Stk')

  // Grid: code | name | [tenant cols…] | actions
  const gridTemplate = `grid-cols-[90px_1fr${sapTenants.map(() => '_120px').join('')}_64px]`

  return (
    <div className="space-y-4">
      {/* Stk warning */}
      {hasStkProblem && (
        <div className="flex items-start gap-3 p-3 bg-orange-50 border border-orange-200 rounded-lg text-sm text-orange-800">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <span className="font-medium">„Stk" nicht definiert.</span>{' '}
            Artikel mit dieser Einheit können in SAP nicht angelegt werden. Lege „Stk" (Stück) an und synchronisiere.
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-gray-500">
          Zentrale Maßeinheiten-Stammdaten — zeilenweise zu SAP-Mandanten synchronisieren.
        </p>
        <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4" />
          Neue Einheit
        </Button>
      </div>

      {/* Matrix table */}
      {units.length === 0 ? (
        <EmptyState
          title="Keine Maßeinheiten definiert"
          description={'Lege Einheiten manuell an oder klicke oben auf „SAP Sync“ — der Import läuft automatisch mit.'}
        />
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          {/* Header row */}
          <div className={cn('grid gap-2 px-4 py-2 bg-gray-50 border-b border-gray-200', gridTemplate)}>
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Code</span>
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Name</span>
            {sapTenants.map((t) => (
              <div key={t.id} className="flex flex-col items-center gap-1">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide text-center leading-tight">
                  {t.name || t.code}
                </span>
                <button
                  onClick={() => pushColumn(t)}
                  disabled={pushingCol === t.id}
                  className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-brand-50 text-brand-700 hover:bg-brand-100 transition-colors disabled:opacity-50"
                  title="Alle aktiven Einheiten in diesen Mandanten pushen"
                >
                  {pushingCol === t.id ? (
                    <Loader2 className="w-2.5 h-2.5 animate-spin" />
                  ) : (
                    <Upload className="w-2.5 h-2.5" />
                  )}
                  Alle pushen
                </button>
              </div>
            ))}
            <span />
          </div>

          {/* Data rows */}
          <div className="divide-y divide-gray-100">
            {units.map((unit) => (
              <div
                key={unit.id}
                className={cn(
                  'grid gap-2 px-4 py-2.5 items-center hover:bg-gray-50 transition-colors',
                  gridTemplate,
                  !unit.isActive && 'opacity-50',
                )}
              >
                <span className="font-mono text-sm font-semibold text-gray-900">{unit.code}</span>
                <span className="text-sm text-gray-700 truncate">{unit.name}</span>
                {sapTenants.map((t) => (
                  <div key={t.id} className="flex justify-center">
                    <StatusCell
                      unit={unit}
                      tenant={t}
                      onPush={() => pushCell(unit, t)}
                      pushing={pushingCell === `${unit.id}:${t.id}`}
                    />
                  </div>
                ))}
                <div className="flex gap-1 justify-end">
                  <button
                    onClick={() => setEditingUnit(unit)}
                    className="p-1.5 text-gray-400 hover:text-gray-700 rounded transition-colors"
                    title="Bearbeiten"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => {
                      if (confirm(`Einheit „${unit.code}" wirklich löschen?`)) {
                        deleteMutation.mutate(unit.id)
                      }
                    }}
                    className="p-1.5 text-gray-400 hover:text-red-600 rounded transition-colors"
                    title="Löschen"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {sapTenants.length === 0 && units.length > 0 && (
        <p className="text-xs text-gray-400 text-center">
          Kein SAP-Mandant konfiguriert — Spalten erscheinen sobald ein Mandant eingerichtet ist.
        </p>
      )}

      {/* Modals */}
      {showCreateModal && (
        <UnitFormModal
          onClose={() => setShowCreateModal(false)}
          onSaved={() => {
            setShowCreateModal(false)
            queryClient.invalidateQueries({ queryKey: ['article-units'] })
          }}
        />
      )}
      {editingUnit && (
        <UnitFormModal
          unit={editingUnit}
          onClose={() => setEditingUnit(null)}
          onSaved={() => {
            setEditingUnit(null)
            queryClient.invalidateQueries({ queryKey: ['article-units'] })
          }}
        />
      )}
    </div>
  )
}

// ─── Unit Form Modal ──────────────────────────────────────────────────────────

function UnitFormModal({
  unit,
  onClose,
  onSaved,
}: {
  unit?: ArticleUnit
  onClose: () => void
  onSaved: () => void
}) {
  const isEdit = !!unit
  const [code, setCode] = useState(unit?.code ?? '')
  const [name, setName] = useState(unit?.name ?? '')
  const [isActive, setIsActive] = useState(unit?.isActive ?? true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const save = async () => {
    if (!code.trim() || !name.trim()) { setError('ME-Code und Name sind Pflichtfelder.'); return }
    setSaving(true)
    setError(null)
    try {
      if (isEdit) {
        await apiClient.patch(`/articles/units/${unit!.id}`, { code: code.trim(), name: name.trim(), isActive })
      } else {
        await apiClient.post('/articles/units', { code: code.trim(), name: name.trim() })
      }
      onSaved()
    } catch (err) {
      setError((err as Error).message)
      setSaving(false)
    }
  }

  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title={isEdit ? `Einheit bearbeiten: ${unit!.code}` : 'Neue Maßeinheit'} size="sm">
      <div className="space-y-4 p-4">
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="ME-Code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="z.B. Stk"
            hint="Eindeutiger Kurzcode"
            disabled={isEdit}
          />
          <Input
            label="ME-Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="z.B. Stück"
            hint="Anzeigename in SAP"
          />
        </div>
        {isEdit && (
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="rounded" />
            Aktiv (wird bei Sync berücksichtigt)
          </label>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Abbrechen</Button>
          <Button variant="primary" onClick={save} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {isEdit ? 'Speichern' : 'Anlegen'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
